/**
 * Shared curtain-wipe animation for theme switching.
 *
 * Slides a full-screen panel in from the right, switches the theme when the
 * screen is fully covered, then slides the panel out to the left — hiding the
 * instantaneous CSS-variable flash from the user.
 *
 * Written with React.createElement (no JSX) so this file stays a plain .ts
 * module — no .tsx extension required.
 */

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ThemeMode } from './theme-types.js';

const CURTAIN_BG: Record<'light' | 'dark', string> = {
  dark: '#242424',
  light: '#ffffff',
};

type Phase = 'idle' | 'covering' | 'revealing';

export function useThemeTransition(setMode: (mode: ThemeMode) => void): {
  triggerTransition: (mode: 'light' | 'dark') => void;
  curtainPortal: React.ReactPortal | null;
} {
  const [phase, setPhase] = useState<Phase>('idle');
  const [color, setColor] = useState(CURTAIN_BG.dark);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  function triggerTransition(mode: 'light' | 'dark'): void {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    // Notify in-process listeners (e.g. AdminFrame) at t=0 so the
    // embedded admin can start its own curtain wipe in lockstep,
    // instead of snapping when the theme-store change fires at t=280.
    try {
      window.dispatchEvent(
        new CustomEvent('ct-agent:theme-transition-start', {
          detail: { mode },
        }),
      );
    } catch {
      /* CustomEvent unsupported — only IE11; ignore */
    }

    setColor(CURTAIN_BG[mode]);
    setPhase('covering');

    timersRef.current.push(
      setTimeout(() => {
        setMode(mode);
        setPhase('revealing');
      }, 280),
    );
    timersRef.current.push(setTimeout(() => setPhase('idle'), 560));
  }

  const curtainPortal =
    phase !== 'idle'
      ? createPortal(
          React.createElement('div', {
            'aria-hidden': 'true',
            className:
              phase === 'covering'
                ? 'theme-curtain-cover'
                : 'theme-curtain-reveal',
            style: { background: color },
          }),
          document.body,
        )
      : null;

  return { triggerTransition, curtainPortal };
}
